<template>
    <b-container class="profile-container without-spacing">
        <h2 class="header-bottom-margin">{{ $t('title') }}</h2>

        <b-alert fade v-model="message.visible" :variant="message.type" dismissible class="alert">
            {{ message.text }}
        </b-alert>

        <validation-observer ref="validation-observer" v-slot="{ handleSubmit }">
            <b-form @submit.prevent="handleSubmit(save)">
                <b-form-group id="user-email-group" :label="$t('user-email-label')" label-for="user-email">
                    <b-form-input id="user-email"
                                  v-model="user.email"
                                  disabled>
                    </b-form-input>
                </b-form-group>

                <b-form-group id="user-locale-group" class="user-locale-group"
                              :label="$t('user-locale-label')" label-for="user-locale">
                    <b-dropdown id="user-locale" no-caret class="locale-dropdown" :disabled="actionsDisabled">
                        <template v-slot:button-content>
                            <div class="d-flex flex-row">
                                <div class="locale">
                                    {{ user.locale }}
                                </div>
                                <dp-icon v-if="!actionsDisabled" name="chevron-down" class="ml-auto mt-0"
                                         width="24" height="24"
                                         :sensitive="false">
                                </dp-icon>
                            </div>
                        </template>
                        <b-dropdown-item v-for="locale in locales" :key="locale" @click="changeLocale(locale)">
                            {{ locale }}
                        </b-dropdown-item>
                    </b-dropdown>
                </b-form-group>

                <validation-provider name="name"
                                     rules="required"
                                     v-slot="validationContext">
                    <b-form-group id="user-name-group" :label="$t('user-name-label')" label-for="user-name"
                                  :invalid-feedback="validationContext.errors[0]">
                        <b-form-input id="user-name"
                                      v-model="user.name"
                                      :state="getValidationState(validationContext)"
                                      :disabled="actionsDisabled">
                        </b-form-input>
                    </b-form-group>
                </validation-provider>

                <validation-provider name="password"
                                     rules="required_if:passwordConfirm|min:8|confirmed:passwordConfirm"
                                     v-slot="validationContext">
                    <b-form-group id="user-password-group" label-for="user-password"
                                  :invalid-feedback="validationContext.errors[0]">
                        <template v-slot:label>
                            {{ $t('user-password-label') }}
                            <b-icon v-if="isVisiblePassword" @click="isVisiblePassword = !isVisiblePassword"
                                    icon="eye-slash-fill"
                                    class="password-visibility-icon"></b-icon>
                            <b-icon v-else icon="eye-fill" @click="isVisiblePassword = !isVisiblePassword"
                                    class="password-visibility-icon"></b-icon>
                        </template>
                        <b-form-input id="user-password"
                                      v-model="user.password"
                                      :type="passwordFieldType"
                                      :state="getValidationState(validationContext)"
                                      :disabled="actionsDisabled">
                        </b-form-input>
                    </b-form-group>
                </validation-provider>

                <validation-provider name="passwordConfirm"
                                     vid="passwordConfirm"
                                     rules="required_if:password|min:8"
                                     v-slot="validationContext">
                    <b-form-group id="user-password-confirm-group" :label="$t('user-password-confirm-label')"
                                  label-for="user-password-confirm"
                                  :invalid-feedback="validationContext.errors[0]">
                        <b-form-input id="user-password-confirm"
                                      v-model="user.passwordConfirm"
                                      :type="passwordFieldType"
                                      :state="getValidationState(validationContext)"
                                      :disabled="actionsDisabled">
                        </b-form-input>
                    </b-form-group>
                </validation-provider>

                <div class="d-flex align-items-center notify-element">
                    <dp-checkbox :value.sync="user.notifyMe" :disabled="actionsDisabled"></dp-checkbox>
                    <div class="ml-2 label">{{ $t('notify-me') }}</div>
                </div>

                <div class="d-flex buttons align-items-center">
                    <dp-button type="submit" :disabled="actionsDisabled">
                        {{ $t('save-action') }}
                    </dp-button>
                    <div class="action-divider">{{ $t('action-divider') }}</div>
                    <dp-button ghost @click.prevent="logout" :disabled="actionsDisabled">
                        {{ $t('logout-action') }}
                    </dp-button>
                </div>
            </b-form>
        </validation-observer>
    </b-container>
</template>

<script>
import DpButton from '@/components/button/dp-button'
import DpCheckbox from '@/components/dp-checkbox'
import DpIcon from '@/components/icon/dp-icon'
import ValidationMixin from '@/mixins/validation-mixin'
import ErrorMixin from '@/mixins/error-mixin'
import UserService from '@/services/user-service'
import i18n from '@/plugins/i18n'
import {localize} from 'vee-validate'

export default {
  name: 'profile',
  components: {DpButton, DpCheckbox, DpIcon},
  mixins: [ValidationMixin, ErrorMixin],
  data() {
    return {
      actionsDisabled: false,
      isVisiblePassword: false,
      user: {
        email: null,
        name: null,
        locale: null,
        password: null,
        passwordConfirm: null,
        notifyMe: true
      },
      message: {
        visible: false,
        type: 'danger',
        text: null
      },
      locales: ['EN', 'RU']
    }
  },
  computed: {
    passwordFieldType() {
      return this.isVisiblePassword ? 'text' : 'password'
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    load() {
      UserService.profile().then((result) => {
        this.$set(this, 'user', {
          email: result.data.email,
          name: result.data.name,
          locale: result.data.locale,
          password: result.data.password,
          passwordConfirm: result.data.password,
          notifyMe: result.data.notify_me
        })
        this.changeLocale(this.user.locale)
      })
    },
    save() {
      this.message.visible = false
      this.actionsDisabled = true

      UserService.saveProfile(this.user.name, this.user.password, this.user.locale, this.user.notifyMe).then(() => {
        this.$log.debug('Profile saved')
        this.message.text = this.$t('success-save')
        this.message.type = 'success'
        this.message.visible = true
      }).catch((error) => {
        this.message.text = this.getErrorMessage(error)
        this.message.type = 'danger'
        this.message.visible = true
      }).finally(() => {
        this.actionsDisabled = false
      })
    },
    logout() {
      this.actionsDisabled = true

      UserService.logout().then(() => {
        this.$store.dispatch('setAuthToken', null)
        this.$router.replace({
          name: 'login'
        })
      }).catch((error) => {
        this.message.text = this.getErrorMessage(error)
        this.message.type = 'danger'
        this.message.visible = true
      }).finally(() => {
        this.actionsDisabled = false
      })
    },
    changeLocale(locale) {
      if (i18n.locale === locale.toLowerCase()) {
        return
      }
      i18n.locale = locale.toLowerCase()
      localize(locale.toLowerCase())
      this.user.locale = locale
    }
  }
}
</script>

<style lang="scss">
.profile-container {
  .alert {
    width: fit-content;
    min-width: 282px;
  }

  .buttons {
    margin-top: 42px !important;
  }

  .password-visibility-icon {
    float: right;
    font-size: $--font-size-medium-base;
    cursor: pointer;
  }

  .action-divider {
    margin-left: 14px;
    margin-right: 14px;
    font-weight: $--font-weight-medium;
  }

  .notify-element {
    margin-top: 40px;

    .label {
      font-size: $--font-size-small !important;
      font-weight: $--font-weight-bold;
    }
  }

  .user-locale-group {
    margin-top: 40px;
  }

  .locale-dropdown {
    height: 40px;
    width: 280px;

    .dropdown-toggle {
      width: 280px;
      margin: 0;
      padding: 7px 16px;
      background-color: $--color-base-01 !important;
      border: 2px solid $--color-base-02 !important;
      border-radius: 8px;
      color: $--color-primary !important;
      font-weight: $--font-weight-medium;
      font-size: $--font-size-base;

      &:disabled {
        background-color: $--color-base-03 !important;
        color: $--color-base-08 !important;
        opacity: 1;
      }
    }

    .dropdown-toggle[aria-expanded="true"] {
      border-color: $--color-secondary !important;
    }

    .dropdown-menu {
      background-color: $--color-base-01;
      border: 2px solid $--color-base-02;
      border-radius: 4px;
      box-shadow: none;
      padding: 8px;
    }

    .dropdown-item {
      padding: 8px;
    }

    .dropdown-item:hover, .dropdown-item:focus {
      color: $--color-primary;
      background-color: $--color-base-05;
      border: none;
      border-radius: 4px;
    }
  }
}
</style>

<i18n>
{
    "en": {
        "title": "Personal info",
        "success-save": "Personal info was saved successfully",
        "user-password-label": "Your new password:",
        "notify-me": "Send me email notifications"
    },
    "ru": {
        "title": "Профиль пользователя",
        "success-save": "Профиль пользователя успешно сохранен",
        "user-password-label": "Новый пароль:",
        "notify-me": "Посылать мне уведомления"
    }
}
</i18n>
